import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  createStockTransfer,
  listStockTransferOrganizations,
  listStockTransfers,
  listStockTransferWarehouses,
  type RefName,
  type StockTransferHeader,
} from "@/api/stock_transfer";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { RefSelect } from "@/components/RefSelect";
import { Card } from "@/components/ui/card";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { apiErrorText, LoadState } from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";

/**
 * «Перемещение» — перемещение запасов БЕЗ заказа: список и создание.
 *
 * Юрлицо и оба склада выбираются ЯВНО (нативные select, как у
 * инвентаризации): ошибка в складе стоит документа, который увезёт товар
 * не туда. Списки — из настроек раздела; пусто = «не настроено», а не
 * «всё подряд».
 */
export function StockTransferListPage(): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [adding, setAdding] = React.useState(false);
  const [orgRef, setOrgRef] = React.useState("");
  const [fromRef, setFromRef] = React.useState("");
  const [toRef, setToRef] = React.useState("");
  // Кнопка нажимается всегда: погашенная без объяснения читалась как
  // «ничего не происходит». Попытка без выбора подсвечивает, чего не хватает.
  const [tried, setTried] = React.useState(false);

  const list = useQuery({ queryKey: ["stock-transfers"], queryFn: () => listStockTransfers() });
  const orgs = useQuery({
    queryKey: ["stock-transfer-orgs"],
    queryFn: () => listStockTransferOrganizations(),
    enabled: adding,
    staleTime: 5 * 60_000,
  });
  const whs = useQuery({
    queryKey: ["stock-transfer-warehouses"],
    queryFn: () => listStockTransferWarehouses(),
    enabled: adding,
    staleTime: 5 * 60_000,
  });

  // Юрлицо одно — выбирать нечего, ставим сразу. Склады так не ставим:
  // отправитель и получатель берутся из ОДНОГО списка, и единственный склад
  // значит «перемещать некуда», а не «выбор очевиден».
  React.useEffect(() => {
    const only = orgs.data?.length === 1 ? orgs.data[0] : undefined;
    if (only && !orgRef) setOrgRef(only.ref);
  }, [orgs.data, orgRef]);

  const org = orgs.data?.find((o) => o.ref === orgRef) ?? null;
  const from = whs.data?.find((w) => w.ref === fromRef) ?? null;
  const to = whs.data?.find((w) => w.ref === toRef) ?? null;
  const sameWh = !!from && !!to && from.ref === to.ref;

  const create = useMutation({
    mutationFn: (v: { org: RefName; from: RefName; to: RefName }) =>
      createStockTransfer({
        organization_ref: v.org.ref,
        from_wh_ref: v.from.ref,
        to_wh_ref: v.to.ref,
      }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["stock-transfers"] });
      navigate(`/stock-transfer/sessions/${d.header.id}`);
    },
  });

  const notConfigured = orgs.data?.length === 0 || whs.data?.length === 0;

  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Перемещение</h1>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1 size-4" />
            Создать перемещение
          </Button>
        )}
      </div>

      {adding && (
        <Card className="space-y-3 p-3">
          {notConfigured && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
              Юрлица/склады раздела не настроены.{" "}
              {user?.is_admin ? (
                <Link to="/settings/stock-transfer" className="font-medium underline">
                  Открыть настройки раздела
                </Link>
              ) : (
                "Попросите администратора выбрать их в настройках раздела «Перемещение»."
              )}
            </div>
          )}

          <RefSelect
            label="Организация"
            value={orgRef}
            onChange={setOrgRef}
            options={orgs.data}
            invalid={tried && !org}
            missingText="Выберите юр. лицо"
          >
            {orgs.isLoading && <p className="text-xs text-muted-foreground">загрузка…</p>}
            {/* Ошибку — ТЕКСТОМ: пустой список неотличим от «не настроено». */}
            {orgs.isError && (
              <p className="text-xs text-red-600">
                Организации не загрузились: {apiErrorText(orgs.error)}
              </p>
            )}
          </RefSelect>

          <RefSelect
            label="Склад-отправитель"
            value={fromRef}
            onChange={setFromRef}
            options={whs.data}
            invalid={tried && !from}
            missingText="Выберите склад-отправитель"
          >
            {whs.isLoading && <p className="text-xs text-muted-foreground">загрузка…</p>}
            {whs.isError && (
              <p className="text-xs text-red-600">
                Склады не загрузились: {apiErrorText(whs.error)}
              </p>
            )}
            {whs.data?.length === 1 && (
              <p className="text-xs text-amber-700">
                В разделе настроен один склад — перемещать некуда. Нужны минимум два.
              </p>
            )}
          </RefSelect>

          <RefSelect
            label="Склад-получатель"
            value={toRef}
            onChange={setToRef}
            options={whs.data}
            invalid={(tried && !to) || sameWh}
            missingText={sameWh ? "" : "Выберите склад-получатель"}
          >
            {sameWh && (
              <p className="text-sm font-medium text-red-600">
                Склад-отправитель и склад-получатель совпадают
              </p>
            )}
          </RefSelect>

          {create.isError && (
            <p className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
              Перемещение не создано: {apiErrorText(create.error)}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={create.isPending}
              onClick={() => {
                setTried(true);
                if (!org || !from || !to || sameWh) return;
                create.mutate({ org, from, to });
              }}
            >
              {create.isPending ? "Создаём…" : "Создать"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setTried(false);
                create.reset();
              }}
            >
              Отмена
            </Button>
          </div>
        </Card>
      )}

      {!list.data ? (
        <LoadState
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          className="flex flex-col items-center gap-3 p-4 text-center"
        />
      ) : (
        <>
          {list.isError && (
            <div className="space-y-1 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
              <p>Список не обновился: {apiErrorText(list.error)}</p>
              <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
                Повторить
              </Button>
            </div>
          )}
          <Section title="Открытые" rows={list.data.open} onOpen={openRow} />
          <Section title="На приёмке" rows={list.data.on_receipt} onOpen={openRow} />
          <Section title="Недавние" rows={list.data.recent} onOpen={openRow} />
          {list.data.open.length === 0 &&
            list.data.on_receipt.length === 0 &&
            list.data.recent.length === 0 &&
            !adding && (
              <p className="text-sm text-muted-foreground">
                Перемещений пока нет. Нажмите «Создать перемещение».
              </p>
            )}
        </>
      )}
    </div>
  );

  function openRow(id: string): void {
    navigate(`/stock-transfer/sessions/${id}`);
  }
}

/** Подпись статуса. «Создан, не проведён» — отдельное состояние: документ
 *  уже в 1С, позиции заморожены, осталось провести. Через приёмку
 *  «принято, не проведено» доводит приёмка/разбор, не отправитель. */
export function statusBadge(h: StockTransferHeader): { text: string; cls: string } {
  switch (h.status) {
    case "sent":
      return { text: "на приёмке", cls: "bg-sky-100 text-sky-800" };
    case "discrepancy":
      return { text: "расхождение", cls: "bg-amber-200 text-amber-900" };
    case "cancelled":
      return { text: "отменено", cls: "bg-slate-200 text-slate-700" };
    case "completed":
      if (h.requires_receipt && !h.doc_posted_at)
        return { text: "принято, не проведено", cls: "bg-amber-200 text-amber-900" };
      if (h.resolution === "by_receipt")
        return { text: "проведён по принятому", cls: "bg-emerald-100 text-emerald-800" };
      return { text: "проведён", cls: "bg-emerald-100 text-emerald-800" };
    case "open":
      if (h.doc_ref) return { text: "создан, не проведён", cls: "bg-red-100 text-red-800" };
      return { text: "черновик", cls: "bg-amber-100 text-amber-900" };
    default:
      // Незнакомый статус — называем как есть, не роняем список.
      return { text: String(h.status), cls: "bg-slate-100 text-slate-700" };
  }
}

/** Номер строки: документ 1С, если есть; иначе номер портала — «черновик»
 *  только у открытого. */
function rowTitle(h: StockTransferHeader): string {
  if (h.doc_number) return `№ ${h.doc_number}`;
  const n = h.display_number ?? "—";
  return h.status === "open" ? `Черновик № ${n}` : `Перемещение №${n}`;
}

function Section({
  title,
  rows,
  onOpen,
}: {
  title: string;
  rows: StockTransferHeader[];
  onOpen: (id: string) => void;
}): React.ReactElement | null {
  if (rows.length === 0) return null;
  return (
    <section className="space-y-2" aria-label={title}>
      <h2 className="text-sm font-semibold text-muted-foreground">{title}</h2>
      {rows.map((h) => {
        const badge = statusBadge(h);
        return (
          <button
            key={h.id}
            onClick={() => onOpen(h.id)}
            className="flex w-full items-center gap-2 rounded-lg border bg-card p-3 text-left"
          >
            <div className="min-w-0 flex-1 space-y-0.5">
              {/* Бейдж — в строке номера, а не колонкой справа: на 360 px
                  колонка съедала ширину, и маршрут обрезался до «Скла…». */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-sm font-medium">{rowTitle(h)}</span>
                <span className={"rounded px-1.5 py-0.5 text-[10px] " + badge.cls}>{badge.text}</span>
              </div>
              <div className="text-sm leading-snug [overflow-wrap:anywhere]">
                {h.from_wh_name} → {h.to_wh_name}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {h.organization_name} · {fmtDate(h.created_at)}
                {h.created_by ? ` · ${h.created_by}` : ""}
              </div>
              {h.sent_at && (
                <div className="truncate text-xs text-muted-foreground">
                  отправлено {fmtDateTime(h.sent_at)}
                  {h.sent_by_name ? ` · ${h.sent_by_name}` : ""}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                строк {h.items_count} · кол-во {fmtQty(h.qty_base_total)}
              </div>
            </div>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </button>
        );
      })}
    </section>
  );
}
